import type { PlayerProfile, TelegramLinkResponse, UpdateProfileRequest } from '@robu/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export const profileApi = {
  get: () => api.get<PlayerProfile>('/me/profile'),
  update: (body: UpdateProfileRequest) => api.patch<PlayerProfile>('/me/profile', body),
  /** TZ §6 — returns the one-time bot deep link. */
  linkTelegram: () => api.post<TelegramLinkResponse>('/me/telegram/link'),
};

/**
 * Administrators without a player profile have `playerId === null`, so callers
 * pass `enabled: false` instead of firing a request that can only 404.
 */
export function useProfile(enabled = true) {
  return useQuery({
    queryKey: queryKeys.me.profile(),
    queryFn: () => profileApi.get(),
    enabled,
    staleTime: 30_000,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateProfileRequest) => profileApi.update(body),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.me.profile(), profile);
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.dashboard() });
    },
  });
}

export function useTelegramLink() {
  return useMutation({ mutationFn: () => profileApi.linkTelegram() });
}
